<template>
  <div class="ci-page">
    <!-- 首屏 -->
    <div class="ci-hero-header">
      <section class="ci-hero">
        <AtsHeroBackground class="ci-hero__bg" :src="assets.heroBg" fallback-color="#131b28" />
        <div class="ci-hero__mask" aria-hidden="true" />
        <div class="ci-hero__inner">
          <div class="ci-hero__content">
            <h1 class="ci-hero__title">公司秉承「有求必救」的理念</h1>
            <p class="ci-hero__intro">
              围绕着出行及生产生活场景，提供「救援、救治、救助、救护」全方位的综合救援服务。先进独有的「大救星」全球综合救援智能云平台将综合服务、保险保障与高科技融为一体，为客户提供全天候一键式服务。
            </p>
            <p class="ci-hero__date">2016.03</p>
            <p class="ci-hero__desc">
              在原国家旅游局的指导和推动下，江泰救援服务有限公司正式成立（注册资金5000万元）。江泰救援是自建全球合作服务网络的中资救援公司，并在原国家旅游局的授权下成立「中国公民全球救援指挥中心」。
            </p>
          </div>
        </div>
      </section>
    </div>

    <!-- 国内分支机构 -->
    <section id="section-branches" class="ci-section ci-section--branches" aria-label="国内分支机构">
      <div class="ci-branches__board">
        <h2 class="ci-branches__title">国内分支机构</h2>
        <div class="ci-branches__body">
          <aside class="ci-branches__aside">
            <div class="ci-branches__block">
              <h3 class="ci-branches__block-title">分支机构分布</h3>
              <ul class="ci-branches__items ci-branches__items--city">
                <li v-for="city in branchCities" :key="city" class="ci-branches__item ci-branches__item--city">{{ city }}</li>
              </ul>
            </div>
            <div class="ci-branches__block">
              <h3 class="ci-branches__block-title">旗下子公司</h3>
              <ul class="ci-branches__items ci-branches__items--company">
                <li v-for="name in subsidiaryCompanies" :key="name" class="ci-branches__item ci-branches__item--company">{{ name }}</li>
              </ul>
            </div>
          </aside>
          <div class="ci-branches__map-wrap">
            <img
              class="ci-branches__map"
              :src="assets.chinaMap"
              alt=""
              role="presentation"
              width="1233"
              height="925"
              loading="lazy"
              decoding="async"
            >
            <div
              v-for="marker in mapMarkerConfigs"
              :key="marker.id"
              class="ci-branches__marker"
              :style="{ left: marker.dot.left, top: marker.dot.top }"
            >
              <img
                class="ci-branches__marker-dot"
                :src="assets[marker.dotKey]"
                alt=""
                role="presentation"
                decoding="async"
              >
              <img
                class="ci-branches__marker-arc"
                :src="assets[marker.arcKey]"
                alt=""
                role="presentation"
                :style="marker.arcStyle"
                decoding="async"
              >
              <div
                class="ci-branches__marker-card"
                :style="{
                  backgroundImage: `url(${assets[marker.cardKey]})`,
                  ...marker.cardStyle
                }"
              >
                <img
                  class="ci-branches__marker-card-pin"
                  :src="assets[marker.pinKey]"
                  alt=""
                  role="presentation"
                  decoding="async"
                >
                <img
                  class="ci-branches__marker-card-text"
                  :src="assets[marker.labelKey]"
                  :alt="marker.name"
                  decoding="async"
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 公司发展历程 -->
    <section
      id="section-history"
      class="ci-section ci-section--history"
      :style="historySectionStyle"
      aria-label="公司发展历程"
    >
      <div class="ci-history__head">
        <h2 class="ci-h2 ci-h2--on-dark" style="color: #ffffff;">公司发展历程</h2>
        <span class="ci-history__title-line" aria-hidden="true" />
      </div>
      <div class="ci-history">
        <button type="button" class="ci-history__arrow ci-history__arrow--prev" aria-label="上一条" @click="prevHistory">
          <img :src="assets.iconArrowLeft" alt="" role="presentation" decoding="async">
        </button>
        <div class="ci-history__panel-wrap">
          <transition :name="historyTransitionName" mode="out-in">
            <div :key="historyIndex" class="ci-history__panel">
              <img
                class="ci-history__photo"
                :src="assets.historyPhoto"
                alt=""
                role="presentation"
                loading="lazy"
                decoding="async"
              >
              <div class="ci-history__text">
                <h3 class="ci-history__slide-title">{{ currentHistorySlide.title }}</h3>
                <p class="ci-history__slide-desc">{{ currentHistorySlide.desc }}</p>
              </div>
            </div>
          </transition>
        </div>
        <button type="button" class="ci-history__arrow ci-history__arrow--next" aria-label="下一条" @click="nextHistory">
          <img :src="assets.iconArrowRight" alt="" role="presentation" decoding="async">
        </button>
      </div>
      <div class="ci-history__timeline" role="tablist" aria-label="发展历程节点">
        <div class="ci-history__timeline-track" aria-hidden="true" />
        <div class="ci-history__timeline-items">
          <button
            v-for="(slide, index) in historySlides"
            :key="slide.id"
            type="button"
            class="ci-history__node"
            :class="{ 'is-active': historyIndex === index }"
            role="tab"
            :aria-selected="historyIndex === index"
            @click="goHistory(index)"
          >
            <span class="ci-history__dot" aria-hidden="true">
              <img
                v-if="historyIndex === index"
                class="ci-history__dot-active"
                :src="assets.mapMarkerActiveBlue"
                alt=""
                role="presentation"
                decoding="async"
              >
            </span>
            <span class="ci-history__node-label">{{ slide.label }}</span>
          </button>
        </div>
      </div>
    </section>

    <!-- 企业资质 -->
    <section id="section-qualifications" class="ci-section ci-section--qualifications" aria-label="企业资质">
      <img
        class="ci-qualifications__visual"
        :src="assets.sectionQualifications"
        alt="企业资质展示"
        loading="lazy"
        decoding="async"
      >
    </section>

    <!-- 企业文化 + 平台资讯 -->
    <section class="ci-section ci-section--bottom" aria-label="企业文化与平台资讯">
      <div class="ci-bottom__inner">
        <div id="section-culture" class="ci-bottom__block ci-bottom__block--culture">
          <div class="ci-bottom__head">
            <h2 class="ci-h2 ci-h2--on-dark">企业文化</h2>
          </div>
          <ul class="ci-culture">
            <li
              v-for="card in cultureCards"
              :key="card.title"
              class="ci-culture__card"
              :class="{ 'ci-culture__card--accent': card.accent }"
            >
              <span class="ci-culture__icon-wrap">
                <img class="ci-culture__icon" :src="card.icon" alt="" role="presentation" decoding="async">
              </span>
              <h3 class="ci-culture__card-title">{{ card.title }}</h3>
              <p class="ci-culture__card-desc">{{ card.desc }}</p>
            </li>
          </ul>
        </div>

        <div id="section-news" class="ci-bottom__block ci-bottom__block--news">
          <div class="ci-bottom__head">
            <h2 class="ci-h2 ci-h2--on-dark">平台资讯</h2>
          </div>
          <div class="ci-news">
            <article class="ci-news__featured">
              <img
                class="ci-news__featured-img"
                :src="assets.photoNewsFeatured"
                alt=""
                role="presentation"
                loading="lazy"
                decoding="async"
              >
              <div class="ci-news__featured-overlay">
                <h3 class="ci-news__featured-title">{{ newsFeatured.title }}</h3>
                <time class="ci-news__featured-date" :datetime="newsFeatured.date">{{ newsFeatured.date }}</time>
              </div>
            </article>
            <article
              v-for="item in newsList"
              :key="item.title"
              class="ci-news__card"
            >
              <h3 class="ci-news__card-title">{{ item.title }}</h3>
              <p class="ci-news__card-desc">{{ item.desc }}</p>
              <div class="ci-news__card-foot">
                <time class="ci-news__card-date" :datetime="item.date">{{ item.date }}</time>
                <img
                  class="ci-news__more"
                  :src="assets.uiCarouselNextPill"
                  alt=""
                  role="presentation"
                  decoding="async"
                >
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>

    <AtsSiteFooter />
  </div>
</template>

<script>
import AtsSiteFooter from '@/components/ats/AtsSiteFooter.vue'
import AtsHeroBackground from '@/components/ats/AtsHeroBackground.vue'

/**
 * 公司介绍专题页：素材来自 `src/assets/aboutTheGreatSavior`
 */
export default {
  name: 'CompanyIntroPage',
  components: {
    AtsSiteFooter,
    AtsHeroBackground
  },
  data () {
    return {
      historyIndex: 0,
      /** @type {'next'|'prev'} 轮播切换方向 */
      historyDirection: 'next',
      assets: {
        heroBg: require('@/assets/aboutTheGreatSavior/hero_bg_skyscraper_night.png'),
        chinaMap: require('@/assets/aboutTheGreatSavior/hero_overlay_black_full.png'),
        branchesBg: require('@/assets/aboutTheGreatSavior/bg_section_geometric_dark.png'),
        mapPin: require('@/assets/aboutTheGreatSavior/map_pin_green_01.png'),
        mapPin02: require('@/assets/aboutTheGreatSavior/map_pin_green_02.png'),
        mapPin03: require('@/assets/aboutTheGreatSavior/map_pin_green_03.png'),
        mapPin04: require('@/assets/aboutTheGreatSavior/map_pin_green_04.png'),
        mapDot01: require('@/assets/aboutTheGreatSavior/map_pagination_dot_green_01.png'),
        mapDot02: require('@/assets/aboutTheGreatSavior/map_pagination_dot_green_02.png'),
        mapDot03: require('@/assets/aboutTheGreatSavior/map_pagination_dot_green_03.png'),
        mapDot04: require('@/assets/aboutTheGreatSavior/map_pagination_dot_green_04.png'),
        mapDot05: require('@/assets/aboutTheGreatSavior/map_pagination_dot_green_05.png'),
        historyArcLight: require('@/assets/aboutTheGreatSavior/history_timeline_arc_light.png'),
        historyArcA: require('@/assets/aboutTheGreatSavior/history_timeline_arc_variant_a.png'),
        historyArcB: require('@/assets/aboutTheGreatSavior/history_timeline_arc_variant_b.png'),
        historyArcC: require('@/assets/aboutTheGreatSavior/history_timeline_arc_variant_c.png'),
        footerWaveSubtle: require('@/assets/aboutTheGreatSavior/bg_footer_wave_subtle.png'),
        uiCardSm01: require('@/assets/aboutTheGreatSavior/ui_card_white_rounded_sm_01.png'),
        uiCardSm02: require('@/assets/aboutTheGreatSavior/ui_card_white_rounded_sm_02.png'),
        uiCardSm03: require('@/assets/aboutTheGreatSavior/ui_card_white_rounded_sm_03.png'),
        uiCardSm04: require('@/assets/aboutTheGreatSavior/ui_card_white_rounded_sm_04.png'),
        uiCardSm05: require('@/assets/aboutTheGreatSavior/ui_card_white_rounded_sm_05.png'),
        mapLabelBeijing: require('@/assets/aboutTheGreatSavior/map_label_beijing.png'),
        mapLabelGansu: require('@/assets/aboutTheGreatSavior/map_label_gansu.png'),
        mapLabelXuzhou: require('@/assets/aboutTheGreatSavior/map_label_xuzhou.png'),
        mapLabelGuizhou: require('@/assets/aboutTheGreatSavior/map_label_guizhou.png'),
        mapLabelHainan: require('@/assets/aboutTheGreatSavior/map_label_hainan.png'),
        historyBg: require('@/assets/aboutTheGreatSavior/hero_bg_skyscraper_sunset_alt.png'),
        iconArrowLeft: require('@/assets/aboutTheGreatSavior/icon_carousel_arrow_left.png'),
        iconArrowRight: require('@/assets/aboutTheGreatSavior/icon_carousel_arrow_right.png'),
        sectionQualifications: require('@/assets/aboutTheGreatSavior/section_qualifications_display.png'),
        photoNewsFeatured: require('@/assets/aboutTheGreatSavior/photo_news_featured_building.png'),
        uiCarouselNextPill: require('@/assets/aboutTheGreatSavior/ui_carousel_next_pill.png'),
        iconCultureHeart: require('@/assets/aboutTheGreatSavior/icon_culture_heart_circle.png'),
        iconCultureBranch: require('@/assets/aboutTheGreatSavior/icon_culture_branch_y.png'),
        historyPhoto: require('@/assets/aboutTheGreatSavior/hero_bg_skyscraper_day_angle.png'),
        mapMarkerActiveBlue: require('@/assets/aboutTheGreatSavior/map_marker_active_blue.png')
      },
      /**
       * 发展历程轮播数据
       * @type {Array<{ id: string, label: string, title: string, desc: string }>}
       */
      historySlides: [
        {
          id: 'founding',
          label: '公司成立',
          title: '公司成立',
          desc: '2016年3月，在天津自贸区注册成立，注册资本金5000万元。首家自建全球综合服务网络的中资救援公司。'
        },
        {
          id: 'command-center',
          label: '指挥中心',
          title: '指挥中心',
          desc: '在原国家旅游局的授权下成立「中国公民全球救援指挥中心」，24小时待命，收到呼救后60秒内人工回拨核实并启动应急预案。'
        },
        {
          id: 'service-network',
          label: '服务网络',
          title: '服务网络',
          desc: '自建全球合作服务网络，覆盖233个国家（含战乱及受经济制裁国家和地区），签约服务商1000余家，服务网点60余万个。'
        },
        {
          id: 'rescue-on-request',
          label: '有求必救',
          title: '有求必救',
          desc: '公司秉承「有求必救」理念，围绕出行及生产生活场景，提供「救援、救治、救助、救护」全方位的综合救援服务。'
        },
        {
          id: 'global-medical',
          label: '全球医疗',
          title: '全球医疗',
          desc: '提供紧急医疗转运、医疗送返、电话医生、视频问诊等全球医疗救援服务，覆盖医疗健康、商务法律、生活娱乐等领域。'
        },
        {
          id: 'official-recognition',
          label: '官方认可',
          title: '官方认可',
          desc: '江泰全球救援联盟于2016年3月20日发起创立，拥有超过200家成员机构，所构建的救援服务网络覆盖全球，获官方及行业广泛认可。'
        },
        {
          id: 'cloud-platform',
          label: '智能云平台',
          title: '智能云平台',
          desc: '先进独有的「大救星」全球综合救援智能云平台，将综合服务、保险保障与高科技融为一体，为客户提供全天候一键式服务。'
        }
      ],
      branchCities: ['北京', '甘肃', '徐州', '贵州', '海南'],
      subsidiaryCompanies: [
        '星耀君康安全技术有限公司',
        '江泰救援服务香港有限公司'
      ],
      mapMarkerConfigs: [
        {
          id: 'gansu',
          name: '甘肃',
          dot: { left: '42.8%', top: '51.5%' },
          dotKey: 'mapDot05',
          arcKey: 'historyArcLight',
          cardKey: 'uiCardSm01',
          pinKey: 'mapPin',
          labelKey: 'mapLabelGansu',
          arcStyle: { left: '0', top: '-57px' },
          cardStyle: { left: '93px', top: '-93px', transform: 'translateX(-50%)' }
        },
        {
          id: 'beijing',
          name: '北京',
          dot: { left: '64%', top: '44.8%' },
          dotKey: 'mapDot01',
          arcKey: 'historyArcA',
          cardKey: 'uiCardSm02',
          pinKey: 'mapPin',
          labelKey: 'mapLabelBeijing',
          arcStyle: { left: '17px', top: '-57px', transform: 'translateX(-50%)' },
          cardStyle: { left: '89px', top: '-80px', transform: 'translateX(-50%)' }
        },
        {
          id: 'xuzhou',
          name: '徐州',
          dot: { left: '62.4%', top: '63%' },
          dotKey: 'mapDot02',
          arcKey: 'historyArcB',
          cardKey: 'uiCardSm03',
          pinKey: 'mapPin02',
          labelKey: 'mapLabelXuzhou',
          arcStyle: { left: '0px', top: '-60px', transform: 'rotate(10deg)' },
          cardStyle: { left: '102px', top: '-74px', transform: 'translateX(-50%)' }
        },
        {
          id: 'guizhou',
          name: '贵州',
          dot: { left: '45.6%', top: '71%' },
          dotKey: 'mapDot03',
          arcKey: 'footerWaveSubtle',
          cardKey: 'uiCardSm04',
          pinKey: 'mapPin03',
          labelKey: 'mapLabelGuizhou',
          arcStyle: { left: '-7px', top: '13px', width: '52.91px', height: '21.01px', transform: 'rotate(-6deg)' },
          cardStyle: { left: '89px', top: '-30px', transform: 'translateX(-50%)' }
        },
        {
          id: 'hainan',
          name: '海南',
          dot: { left: '57.4%', top: '84.5%' },
          dotKey: 'mapDot04',
          arcKey: 'historyArcLight',
          cardKey: 'uiCardSm05',
          pinKey: 'mapPin04',
          labelKey: 'mapLabelHainan',
          arcStyle: { left: '17px', top: '-58px', transform: 'translateX(-50%)' },
          cardStyle: { left: '87px', top: '-102px', transform: 'translateX(-50%)' }
        }
      ],
      cultureCards: [
        {
          title: '企业愿景',
          desc: '将东方美学与现代商业理念完美融合，构建独具魅力的展示空间让世界听见中国品牌的声音',
          icon: require('@/assets/aboutTheGreatSavior/icon_culture_heart_circle.png'),
          accent: false
        },
        {
          title: '企业使命',
          desc: '将东方美学与现代商业理念完美融合，构建独具魅力的展示空间让世界听见中国品牌的声音',
          icon: require('@/assets/aboutTheGreatSavior/icon_culture_heart_circle.png'),
          accent: true
        },
        {
          title: '核心价值观',
          desc: '将东方美学与现代商业理念完美融合，构建独具魅力的展示空间让世界听见中国品牌的声音',
          icon: require('@/assets/aboutTheGreatSavior/icon_culture_branch_y.png'),
          accent: false
        }
      ],
      newsFeatured: {
        title: '大救星携手合作伙伴共建全球救援服务网络',
        date: '2024-08-23'
      },
      newsList: [
        {
          title: '重点城市设立分支机构满足参展企业一站式会展需求',
          desc: '在产业数字化浪潮的推动下，众多行业正经历着前所未有的变革。AI与RPA技术的融合应用，为企业提供了全新的效率提升路径，推动行业向智能化、自动化方向持续演进。',
          date: '2024-08-23'
        },
        {
          title: '重点城市设立分支机构满足参展企业一站式会展需求',
          desc: '在产业数字化浪潮的推动下，众多行业正经历着前所未有的变革。AI与RPA技术的融合应用，为企业提供了全新的效率提升路径，推动行业向智能化、自动化方向持续演进。',
          date: '2024-08-23'
        },
        {
          title: '重点城市设立分支机构满足参展企业一站式会展需求',
          desc: '在产业数字化浪潮的推动下，众多行业正经历着前所未有的变革。AI与RPA技术的融合应用，为企业提供了全新的效率提升路径，推动行业向智能化、自动化方向持续演进。',
          date: '2024-08-23'
        },
        {
          title: '重点城市设立分支机构满足参展企业一站式会展需求',
          desc: '在产业数字化浪潮的推动下，众多行业正经历着前所未有的变革。AI与RPA技术的融合应用，为企业提供了全新的效率提升路径，推动行业向智能化、自动化方向持续演进。',
          date: '2024-08-23'
        }
      ]
    }
  },
  computed: {
    /** @returns {{ id: string, label: string, title: string, desc: string }} */
    currentHistorySlide () {
      return this.historySlides[this.historyIndex] || this.historySlides[0]
    },
    /** @returns {string} */
    historyTransitionName () {
      return `history-slide-${this.historyDirection}`
    },
    /** @returns {{ backgroundImage: string, backgroundSize: string, backgroundPosition: string, backgroundRepeat: string }} */
    historySectionStyle () {
      return {
        backgroundImage: `url(${this.assets.historyBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    }
  },
  methods: {
    /**
     * 切换发展历程节点
     * @param {number} index
     * @returns {void}
     */
    goHistory (index) {
      if (index === this.historyIndex) return
      this.historyDirection = index > this.historyIndex ? 'next' : 'prev'
      this.historyIndex = index
    },
    /** @returns {void} */
    prevHistory () {
      this.historyDirection = 'prev'
      const len = this.historySlides.length
      this.historyIndex = (this.historyIndex - 1 + len) % len
    },
    /** @returns {void} */
    nextHistory () {
      this.historyDirection = 'next'
      const len = this.historySlides.length
      this.historyIndex = (this.historyIndex + 1) % len
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../components/ats/mixins';
$green: #256a30;
$text-dark: #1a2226;

.ci-page {
  min-height: 100vh;
  background: #f4f4f4;
  color: $text-dark;
  font-family: 'Microsoft YaHei', 'PingFang SC', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
}

.ci-hero-header {
  position: relative;
  height: 871px;
  min-height: 871px;
  max-height: 871px;
}

.ci-hero {
  position: relative;
  height: 100%;
  min-height: 871px;
  overflow: hidden;
}

.ci-hero__bg {
  background-size: cover;
  background-position: center center;
}

.ci-hero__mask {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    90deg,
    rgba(8, 24, 48, 0.78) 0%,
    rgba(8, 24, 48, 0.45) 42%,
    rgba(8, 24, 48, 0.12) 72%,
    rgba(8, 24, 48, 0.28) 100%
  );
  pointer-events: none;
}

.ci-hero__inner {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  max-width: 1320px;
  margin: 0 auto;
  padding: 120px 48px 80px;
  box-sizing: border-box;
}

.ci-hero__content {
  max-width: 720px;
  color: #ffffff;
  text-align: left;
}

.ci-hero__title {
  margin: 0 0 36px;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: 0.04em;
  color: #ffffff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
}

.ci-hero__intro {
  margin: 0 0 48px;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.85;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.02em;
}

.ci-hero__date {
  margin: 0 0 36px;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.04em;
  color: #ffffff;
}

.ci-hero__desc {
  margin: 0;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.85;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.02em;
}

.ci-section {
  max-width: 1320px;
  margin: 0 auto;
  padding: 80px 48px;
  box-sizing: border-box;
}

.ci-h2 {
  margin: 0 0 48px;
  font-size: 36px;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
  color: $text-dark;
}

.ci-h2--on-dark {
  color: #333;
}

/* 国内分支机构 */
.ci-section--branches {
  max-width: none;
  padding: 80px 48px 96px;
  background-image: url('~@/assets/aboutTheGreatSavior/bg_section_geometric_dark.png');
  background-size: cover;
  background-position: right center;
  background-repeat: no-repeat;
}

.ci-branches__board {
  max-width: 1320px;
  margin: 0 auto;
}

.ci-branches__title {
  margin: 0 0 40px;
  font-size: 36px;
  font-weight: 700;
  line-height: 1.3;
  color: $text-dark;
  text-align: left;
}

.ci-branches__body {
  display: grid;
  grid-template-columns: 420px 1fr;
  gap: 32px 48px;
  align-items: flex-start;
}

.ci-branches__aside {
  display: flex;
  flex-direction: column;
  gap: 48px;
  padding-top: 8px;
}

.ci-branches__block-title {
  margin: 0 0 20px;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.35;
  color: #333333;
}

.ci-branches__items {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ci-branches__item {
  box-sizing: border-box;
  font-size: 22px;
  font-weight: 400;
  line-height: 1.45;
  color: #333333;
  background-image: url('~@/assets/aboutTheGreatSavior/ui_bg_gray_light.png');
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center;
  border-left: 4px solid $green;
}

.ci-branches__item--city {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 160px;
  height: 70px;
  padding: 0;
  text-align: center;
}

.ci-branches__item--company {
  width: 323px;
  height: 70px;
  padding: 0 18px;
  text-align: left;
  display: flex;
  align-items: center;
}

.ci-branches__map-wrap {
  position: relative;
  z-index: 2;
  width: 1233px;
  min-height: 925px;
  isolation: isolate;
  margin-left: -150px;
  margin-top: -170px;
}

.ci-branches__map {
  position: relative;
  z-index: 2;
  display: block;
  width: 1233px;
  height: 925px;
  max-width: 100%;
  object-fit: contain;
  object-position: center;
  opacity: 1;
  filter: none;
  pointer-events: none;
}

.ci-branches__marker {
  position: absolute;
  z-index: 3;
  width: 0;
  height: 0;
}

.ci-branches__marker-dot {
  position: absolute;
  left: 0;
  top: 0;
  width: 24.67px;
  height: 24.67px;
  transform: translate(-50%, -50%);
  object-fit: contain;
}

.ci-branches__marker-arc {
  position: absolute;
  display: block;
  width: 42.02px;
  height: 44.47px;
  pointer-events: none;
  object-fit: contain;
  transform-origin: left bottom;
}

.ci-branches__marker-card {
  position: absolute;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  width: 98px;
  height: 44px;
  padding: 0 10px;
  box-sizing: border-box;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center;
}

.ci-branches__marker-card-pin {
  display: block;
  width: 17.62px;
  height: 21.14px;
  flex-shrink: 0;
  object-fit: contain;
}

.ci-branches__marker-card-text {
  display: block;
  width: 30px;
  height: 20px;
  flex-shrink: 0;
  object-fit: contain;
}

/* 发展历程 */
.ci-section--history {
  max-width: none;
  padding: 80px 48px 96px;
  color: #ffffff;
}

.ci-history__head {
  max-width: 1240px;
  margin: 0 auto 56px;
  text-align: center;
}

.ci-history__head .ci-h2 {
  margin-bottom: 16px;
}

.ci-history__title-line {
  display: block;
  width: 48px;
  height: 3px;
  margin: 0 auto;
  background: #ffffff;
}

.ci-history {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  max-width: 1240px;
  margin: 0 auto 72px;
}

.ci-history__arrow {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  opacity: 0.92;
  @include ats-hover-icon;
}

.ci-history__arrow img {
  display: block;
  width: 100%;
  height: auto;
}

.ci-history__panel-wrap {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  position: relative;
}

.ci-history__panel {
  display: grid;
  grid-template-columns: minmax(0, 420px) 1fr;
  gap: 48px;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
}

/* 下一条：新内容从右入、旧内容向左出 */
.history-slide-next-enter-active,
.history-slide-next-leave-active,
.history-slide-prev-enter-active,
.history-slide-prev-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.history-slide-next-enter {
  opacity: 0;
  transform: translateX(24px);
}

.history-slide-next-leave-to {
  opacity: 0;
  transform: translateX(-24px);
}

/* 上一条：新内容从左入、旧内容向右出 */
.history-slide-prev-enter {
  opacity: 0;
  transform: translateX(-24px);
}

.history-slide-prev-leave-to {
  opacity: 0;
  transform: translateX(24px);
}

@media (prefers-reduced-motion: reduce) {
  .history-slide-next-enter-active,
  .history-slide-next-leave-active,
  .history-slide-prev-enter-active,
  .history-slide-prev-leave-active {
    transition: opacity 0.12s ease;
  }

  .history-slide-next-enter,
  .history-slide-next-leave-to,
  .history-slide-prev-enter,
  .history-slide-prev-leave-to {
    transform: none;
  }
}

.ci-history__photo {
  display: block;
  width: 100%;
  height: 260px;
  object-fit: cover;
  border-radius: 2px;
}

.ci-history__slide-title {
  margin: 0 0 24px;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.35;
  color: #ffffff;
}

.ci-history__slide-desc {
  margin: 0;
  font-size: 18px;
  line-height: 1.85;
  color: rgba(255, 255, 255, 0.92);
}

.ci-history__timeline {
  position: relative;
  max-width: 1240px;
  margin: 0 auto;
  padding-top: 10px;
}

.ci-history__timeline-track {
  position: absolute;
  left: 48px;
  right: 48px;
  top: 10px;
  height: 1px;
  background: rgba(255, 255, 255, 0.45);
}

.ci-history__timeline-items {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.ci-history__node {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  min-width: 0;
  padding: 0 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  @include ats-hover-node;

  &:not(.is-active):hover .ci-history__node-label {
    color: #ffffff;
  }
}

.ci-history__dot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ffffff;
  flex-shrink: 0;
}

.ci-history__node.is-active .ci-history__dot {
  background: transparent;
}

.ci-history__dot-active {
  display: block;
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.ci-history__node-label {
  font-size: 14px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.88);
  white-space: nowrap;
  transition: color 0.18s ease;
}

.ci-history__node.is-active .ci-history__node-label {
  color: #4da3ff;
  font-weight: 500;
}

/* 企业资质 */
.ci-section--qualifications {
  max-width: none;
  padding: 0;
  height: 1090px;
  overflow: hidden;
}

.ci-qualifications__visual {
  display: block;
  width: 100%;
  height: 1090px;
  object-fit: cover;
  object-position: center;
}

/* 企业文化 + 平台资讯 */
.ci-section--bottom {
  max-width: none;
  height: 1740px;
  padding: 0 48px 88px;
  box-sizing: border-box;
  background-image: url('~@/assets/aboutTheGreatSavior/bg_footer_wave_dark_full.png');
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
  color: #ffffff;
}

.ci-bottom__inner {
  max-width: 1400px;
  margin: 0 auto;
}

.ci-bottom__block--culture {
  margin-bottom: 88px;
}

.ci-bottom__head {
  margin-bottom: 40px;
  text-align: center;
}

.ci-bottom__block--culture .ci-bottom__head {
  padding-top: 100px;
}

.ci-bottom__head .ci-h2 {
  margin-bottom: 0;
}

.ci-bottom__title-line {
  display: block;
  width: 48px;
  height: 3px;
  margin: 0 auto;
  background: #ffffff;
}

.ci-culture {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
}

.ci-culture__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 413px;
  padding: 56px 48px 52px;
  text-align: center;
  box-sizing: border-box;
  background: #ffffff;
  border: none;
  border-radius: 0;
}

.ci-culture__card--accent {
  background-color: $green;
  background-image: url('~@/assets/aboutTheGreatSavior/ui_chip_green_solid.png');
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center;
  color: #ffffff;
}

.ci-culture__icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  margin: 0 auto 32px;
  flex-shrink: 0;
}

.ci-culture__icon {
  display: block;
  width: 96px;
  height: 96px;
  object-fit: contain;
}

.ci-culture__card--accent .ci-culture__icon {
  filter: brightness(0) invert(1);
}

.ci-culture__card-title {
  margin: 0 0 24px;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.35;
  color: #333333;
}

.ci-culture__card--accent .ci-culture__card-title,
.ci-culture__card--accent .ci-culture__card-desc {
  color: #ffffff;
}

.ci-culture__card-desc {
  margin: 0;
  max-width: 320px;
  font-size: 14px;
  line-height: 1.85;
  color: #888888;
}

.ci-news {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  align-items: stretch;
}

.ci-news__featured {
  position: relative;
  grid-column: span 2;
  overflow: hidden;
  min-height: 320px;
  border-radius: 12px;
}

.ci-news__featured-img {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 320px;
  object-fit: cover;
}

.ci-news__featured-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 24px;
  background: rgba(0, 0, 0, 0.55);
  box-sizing: border-box;
}

.ci-news__featured-title {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  color: #ffffff;
}

.ci-news__featured-date {
  flex-shrink: 0;
  font-size: 14px;
  line-height: 1.4;
  color: #ffffff;
}

.ci-news__card {
  display: flex;
  flex-direction: column;
  min-height: 320px;
  padding: 36px 32px 28px;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
}

.ci-news__card-title {
  margin: 0 0 20px;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.55;
  text-align: center;
  color: #333333;
}

.ci-news__card-desc {
  flex: 1;
  margin: 0 0 24px;
  font-size: 13px;
  line-height: 1.75;
  text-align: left;
  color: #666666;
}

.ci-news__card-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
}

.ci-news__card-date {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
  color: #333333;
}

.ci-news__more {
  display: block;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  object-fit: contain;
}

@media (max-width: 960px) {
  .ci-hero-header {
    height: auto;
    min-height: 520px;
    max-height: none;
  }
  .ci-hero {
    min-height: 520px;
  }
  .ci-hero__inner {
    padding: 100px 20px 48px;
  }
  .ci-hero__title {
    margin-bottom: 24px;
    font-size: 28px;
  }
  .ci-hero__intro {
    margin-bottom: 32px;
    font-size: 16px;
  }
  .ci-hero__date {
    margin-bottom: 24px;
    font-size: 28px;
  }
  .ci-hero__desc {
    font-size: 16px;
  }
  .ci-section {
    padding: 56px 20px;
  }
  .ci-branches__body {
    grid-template-columns: 1fr;
  }
  .ci-branches__map-wrap {
    width: 100%;
    min-height: 0;
    height: auto;
   
  }
  .ci-branches__map {
    width: 100%;
    height: auto;
    aspect-ratio: 1233 / 925;
  }
  .ci-history__panel,
  .ci-culture,
  .ci-news {
    grid-template-columns: 1fr;
  }
  .ci-news__featured {
    grid-column: span 1;
    min-height: 240px;
  }
  .ci-section--bottom {
    height: auto;
    min-height: 0;
  }
  .ci-bottom__block--culture {
    margin-bottom: 56px;
  }
  .ci-culture__card {
    min-height: 0;
    padding: 40px 24px;
  }
  .ci-culture__icon-wrap,
  .ci-culture__icon {
    width: 72px;
    height: 72px;
  }
  .ci-culture__icon-wrap {
    margin-bottom: 24px;
  }
  .ci-news__featured-img {
    min-height: 240px;
  }
  .ci-news__card {
    min-height: 0;
    padding: 28px 20px 24px;
  }
  .ci-history {
    gap: 16px;
  }
  .ci-history__arrow {
    width: 44px;
    height: 44px;
  }
  .ci-history__photo {
    height: 200px;
  }
  .ci-history__timeline-items {
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px 12px;
  }
  .ci-history__node {
    flex: 0 0 auto;
  }
  .ci-history__timeline-track {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/companyIntro/companyIntro.vue